// Breed-aware style recommendations. Simple keyword matching for now; can be
// replaced by a server-side / model-driven recommender later without UI changes.

export const BREED_SUGGESTIONS = [
  "Poodle", "Goldendoodle", "Labradoodle", "Cockapoo", "Cavapoo", "Maltipoo", "Bernedoodle",
  "Bichon Frise", "Maltese", "Shih Tzu", "Lhasa Apso", "Havanese", "Coton de Tulear",
  "Yorkshire Terrier", "Silky Terrier", "Miniature Schnauzer", "Standard Schnauzer",
  "West Highland Terrier", "Scottish Terrier", "Airedale Terrier", "Wire Fox Terrier",
  "Cocker Spaniel", "Cavalier King Charles Spaniel", "Springer Spaniel",
  "Golden Retriever", "Bernese Mountain Dog", "Newfoundland", "Border Collie", "Shetland Sheepdog", "Australian Shepherd",
  "Pomeranian", "Samoyed", "Siberian Husky", "Alaskan Malamute", "Chow Chow", "Keeshond",
  "French Bulldog", "Pug", "Beagle", "Boxer", "Labrador Retriever", "Dachshund", "Chihuahua", "Bulldog", "Boston Terrier",
  "Mixed breed",
];

const GROUPS = [
  {
    id: "curly",
    label: "curly & doodle coats",
    match: /poodle|doodle|poo\b|cockapoo|cavapoo|maltipoo|bernedoodle|schnoodle|curly/i,
    picks: [
      ["teddy-bear", "Curly coats hold a rounded teddy shape beautifully and stay soft between visits."],
      ["puppy-cut", "An even length keeps curls tidy and brushing quick — the everyday favourite for doodles."],
      ["rounded-face", "Softens the muzzle and frames the eyes, a signature look for poodle-type faces."],
    ],
  },
  {
    id: "silky-small",
    label: "small silky & drop coats",
    match: /bichon|maltese|shih\s?tzu|lhasa|havanese|coton|yorkie|yorkshire|silky|papillon|pekingese/i,
    picks: [
      ["puppy-cut", "Keeps a fine, fast-growing coat manageable without losing the breed's charm."],
      ["rounded-face", "A neat round head and trimmed eye area is the classic pet trim for these breeds."],
      ["fluffy-long", "If you brush daily, a longer trim shows off the flowing coat these breeds are known for."],
    ],
  },
  {
    id: "wire",
    label: "schnauzers & wire-coated terriers",
    match: /schnauzer|terrier|westie|scottie|airedale|cairn|border terrier|wheaten/i,
    picks: [
      ["breed-inspired", "The classic pattern — short body, longer beard, eyebrows and leg furnishings."],
      ["sporty", "A shorter, practical version of the breed trim that's easy to keep clean."],
      ["short-summer", "A clean, low-maintenance option for hot months or active dogs."],
    ],
  },
  {
    id: "spaniel",
    label: "spaniels",
    match: /spaniel|cocker|cavalier|springer|setter/i,
    picks: [
      ["breed-inspired", "Trimmed body with flowing ear and leg feathering — the traditional spaniel look."],
      ["fluffy-long", "Keeps the soft feathering that gives spaniels their elegant silhouette."],
      ["sporty", "A neatened, shorter coat that's practical for dogs who love the outdoors."],
    ],
  },
  {
    id: "double",
    label: "double-coated breeds",
    match: /retriever|golden|bernese|newfoundland|collie|sheltie|shepherd|husky|malamute|samoyed|pomeranian|spitz|akita|chow|keeshond|eskimo|corgi|leonberger/i,
    picks: [
      ["sporty", "A tidy trim of feathering, paws and sanitary areas keeps the natural double coat healthy — groomers avoid shaving these coats."],
      ["fluffy-long", "Deshed, bathed and shaped to show off the full coat at its fluffiest."],
      ["breed-inspired", "A light breed-standard shaping that respects how the coat grows."],
    ],
  },
  {
    id: "short",
    label: "short-coated breeds",
    match: /bulldog|frenchie|pug|beagle|boxer|labrador|\blab\b|dachshund|chihuahua|boston|pit|staff|whippet|greyhound|dalmatian|vizsla|pointer|doberman|rottweiler|great dane|mastiff|basenji/i,
    picks: [
      ["short-summer", "A bath, deshed and light tidy — short coats don't need a haircut, just polish."],
      ["sporty", "Neatens edges, nails and ears for a crisp, athletic finish."],
      ["custom", "Describe any specific touches — wrinkle care, paw tidy or a nail trim."],
    ],
  },
];

const DEFAULT_PICKS = [
  ["teddy-bear", "The most-requested pet trim — soft, rounded and forgiving for most coat types."],
  ["puppy-cut", "An even, manageable length that works for almost any breed."],
  ["short-summer", "Lower maintenance and cooler for active or warm-weather dogs."],
];

export function getBreedRecommendations(breed) {
  const q = (breed || "").trim();
  if (!q) return { known: false, group: null, picks: [] };
  const group = GROUPS.find((g) => g.match.test(q));
  const picks = (group ? group.picks : DEFAULT_PICKS).map(([styleId, reason]) => ({ styleId, reason }));
  return { known: !!group, group: group?.label || null, picks };
}

export function getRecommendationFor(breed, styleId) {
  return getBreedRecommendations(breed).picks.find((p) => p.styleId === styleId) || null;
}
